import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import PopupModal from "../../components/Modal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const defaultTheme = createTheme();

const ResetPassword = () => {
  const [open, setOpen] = React.useState(false);
  const [searchParams] = useSearchParams();
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const navigate = useNavigate();

  const showSuccessToastMessage = (msg) => {
    toast.success(msg, {
      position: "bottom-right",
    });
  };
  const showErrorToastMessage = (msg) => {
    return toast.error(msg, {
      position: "bottom-right",
    });
  };
  const formik = useFormik({
    initialValues: {
      newpassword: "",
      confirmpassword: "",
    },
    validationSchema: Yup.object({
      newpassword: Yup.string().min(5).required("Required"),
      confirmpassword: Yup.string()
        .oneOf([Yup.ref("newpassword"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),

    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        const token = searchParams.get("token");
        if (!token) {
          showErrorToastMessage("Error! token not found");
          navigate("/forget-password");
        }
        Object.keys(values).forEach((key) => {
          formData.append(key, values[key]);
        });
        formData.append("token", token);

        const response = await axios.post(process.env.REACT_APP_BACKEND_URL + "resetpassword", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response?.data) {
          localStorage.removeItem("forget_token");
          showSuccessToastMessage("Password reset successfully");
          navigate("/login");
        }
        // Handle successful response
      } catch (error) {
        localStorage.removeItem("forget_token");
        localStorage.setItem("tokenExpire", true);
        navigate("/forget-password");
      }
    },
  });
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const handleMouseDownConfirmPassword = (event) => {
    event.preventDefault();
  };
  return (
    <ThemeProvider theme={defaultTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          maxHeight: { xs: 1000, sm: 1150 },
          backgroundColor: "#222131",
        }}
      >
        <PopupModal text={{ title: "Error!", content: "Sorry! token expires try again" }} cross={true} handleClose={handleClose} open={open} />
        <Container component="main" maxWidth="xs" sx={{ paddingY: 4 }}>
          <CssBaseline />
          <Box
            sx={{
              marginTop: 4,
              boxShadow: "0px 6px 20px rgba(104, 109, 252, 0.7)",
              border: "1px solid #686DFC",
              padding: "30px",
              borderRadius: "5px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#222131",
              color: "white",
            }}
          >
            <Link to={"/"}>
              <Avatar alt="Logo" sx={{ m: 2, width: 65, height: 65, cursor: "pointer" }} src="Magic Pleadings.png" />
            </Link>
            <Typography component="h1" variant="h5" sx={{ fontFamily: "Recoletabold" }}>
              Reset Password
            </Typography>

            <Box component="form" noValidate onSubmit={formik.handleSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    error={formik.touched.newpassword && formik.errors?.newpassword}
                    required
                    fullWidth
                    name="newpassword"
                    label="New Password"
                    type="password"
                    id="password"
                    variant="filled"
                    autoComplete="new-password"
                    size="small"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.newpassword}
                    helperText={formik.touched?.newpassword && formik.errors?.newpassword}
                    sx={{ backgroundColor: "white", borderRadius: "5px" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={formik.touched.confirmpassword && formik.errors?.confirmpassword}
                    required
                    fullWidth
                    name="confirmpassword"
                    label="Confirm Password"
                    type={showPassword ? "type" : "password"}
                    id="password"
                    variant="filled"
                    autoComplete="new-password"
                    size="small"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.confirmpassword}
                    helperText={formik.touched?.confirmpassword && formik.errors?.confirmpassword}
                    sx={{ backgroundColor: "white", borderRadius: "5px" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="toggle password visibility" onClick={handleClickShowConfirmPassword} onMouseDown={handleMouseDownConfirmPassword} edge="end">
                            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  boxShadow: "0px 4px 8px rgba(104, 109, 252, 0.5)",
                  "&:hover, &:focus": {
                    boxShadow: "0px 6px 12px rgba(104, 109, 252, 0.7)",
                    backgroundColor: "white",
                    color: "#6a1b9a",
                  },
                  backgroundColor: "#686DFC",
                  fontWeight: "800",
                  fontFamily: "poppins",
                }}
              >
                Change &nbsp; Password
              </Button>
              <Grid
                container
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Grid
                  item
                  sx={{
                    fontSize: { xs: "16px", sm: "14px" },
                    fontFamily: "poppins",
                  }}
                >
                  Remember Password?
                  <Link to={"/login"} style={{ color: "white" }} variant="body2">
                    {" Login"}
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ResetPassword;
